import api from './api.service';

export interface User {
  id: string;
  username: string;
  email: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status: boolean;
  mustChangePassword?: boolean;
  createdAt: string;
  updatedAt: string;
  roleId?: string;
  role?: {
    id: string;
    name: string;
  };
}

export interface CreateUserData {
  username: string;
  email: string;
  password: string;
  roleId: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: boolean;
  mustChangePassword?: boolean;
}

export interface UpdateUserData {
  username?: string;
  email?: string;
  roleId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  status?: boolean;
  mustChangePassword?: boolean;
}

export interface ChangePasswordData {
  newPassword: string;
}

export interface ChangeMyPasswordData {
  currentPassword: string;
  newPassword: string;
}

export interface ForgotPasswordData {
  email: string;
}

export interface ResetPasswordData {
  token: string;
  newPassword: string;
}

class UserService {
  private readonly BASE_PATH = '/users';

  // ============================================
  // ADMINISTRACIÓN DE USUARIOS
  // ============================================

  /**
   * Obtener todos los usuarios
   */
  async getAllUsers(): Promise<User[]> {
    const response = await api.get<User[]>(this.BASE_PATH);
    return response.data;
  }

  /**
   * Obtener usuario por ID
   */
  async getUserById(userId: string): Promise<User> {
    const response = await api.get<User>(`${this.BASE_PATH}/${userId}`);
    return response.data;
  }

  /**
   * Crear nuevo usuario
   */
  async createUser(userData: CreateUserData): Promise<User> {
    const response = await api.post<User>(this.BASE_PATH, userData);
    return response.data;
  }

  /**
   * Actualizar usuario
   */
  async updateUser(userId: string, userData: UpdateUserData): Promise<User> {
    const response = await api.put<User>(`${this.BASE_PATH}/${userId}`, userData);
    return response.data;
  }

  /**
   * Eliminar usuario
   */
  async deleteUser(userId: string): Promise<{ message: string }> {
    const response = await api.delete<{ message: string }>(`${this.BASE_PATH}/${userId}`);
    return response.data;
  }

  /**
   * Cambiar contraseña de usuario (como admin)
   */
  async changeUserPassword(userId: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.put<{ message: string }>(
      `${this.BASE_PATH}/${userId}/change-password`,
      { newPassword }
    );
    return response.data;
  }

  /**
   * Activar/Desactivar usuario
   */
  async toggleUserStatus(userId: string, status: boolean): Promise<{ message: string; status: boolean }> {
    const response = await api.put<{ message: string; status: boolean }>(
      `${this.BASE_PATH}/${userId}/status`,
      { status }
    );
    return response.data;
  }

  // ============================================
  // PERFIL PERSONAL
  // ============================================

  /**
   * Obtener mi perfil
   */
  async getMyProfile(): Promise<User> {
    const response = await api.get<User>(`${this.BASE_PATH}/me`);
    return response.data;
  }

  /**
   * Actualizar mi perfil
   */
  async updateMyProfile(userData: UpdateUserData): Promise<User> {
    const response = await api.put<User>(`${this.BASE_PATH}/me`, userData);
    return response.data;
  }

  /**
   * Cambiar mi contraseña
   */
  async changeMyPassword(currentPassword: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.put<{ message: string }>(
      `${this.BASE_PATH}/me/change-password`,
      { currentPassword, newPassword }
    );
    return response.data;
  }

  // ============================================
  // RECUPERACIÓN DE CONTRASEÑA
  // ============================================

  /**
   * Solicitar reset de contraseña
   */
  async forgotPassword(email: string): Promise<{ message: string; resetToken?: string; resetUrl?: string }> {
    const response = await api.post<{ message: string; resetToken?: string; resetUrl?: string }>(
      `${this.BASE_PATH}/forgot-password`,
      { email }
    );
    return response.data;
  }

  /**
   * Resetear contraseña con token
   */
  async resetPassword(token: string, newPassword: string): Promise<{ message: string }> {
    const response = await api.post<{ message: string }>(
      `${this.BASE_PATH}/reset-password`,
      { token, newPassword }
    );
    return response.data;
  }
}

export default new UserService();
